import React, { useEffect, useState } from 'react';
import type { Disparo } from '../../types';
import { apiService } from '../../services/api';
import './EditDisparoScheduleModal.css';

export interface EditDisparoScheduleModalProps {
  open: boolean;
  disparo: Disparo | null;
  onClose: () => void;
  onSaved: () => void;
}

export const EditDisparoScheduleModal: React.FC<EditDisparoScheduleModalProps> = ({
  open,
  disparo,
  onClose,
  onSaved,
}) => {
  const [horaInicio, setHoraInicio] = useState('');
  const [horaFim, setHoraFim] = useState('');
  const [intervaloMinutos, setIntervaloMinutos] = useState(5);
  const [maxDisparosPorNumero, setMaxDisparosPorNumero] = useState(100);
  const [autoResumeOnMax, setAutoResumeOnMax] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !disparo) return;
    setHoraInicio(disparo.horaInicio);
    setHoraFim(disparo.horaFim);
    setIntervaloMinutos(disparo.intervaloMinutos);
    setMaxDisparosPorNumero(disparo.maxDisparosPorNumero ?? 100);
    setAutoResumeOnMax(disparo.autoResumeOnMax ?? false);
    setError('');
  }, [open, disparo]);

  if (!open || !disparo) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const d = disparo;
    if (!d) return;
    setError('');
    setSaving(true);
    try {
      await apiService.patchDisparoConfig(d.disparoId, {
        horaInicio,
        horaFim,
        intervaloMinutos,
        maxDisparosPorNumero,
        autoResumeOnMax,
      });
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="edit-disparo-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="edit-disparo-modal"
        role="dialog"
        aria-labelledby="edit-disparo-title"
        onClick={(ev) => ev.stopPropagation()}
      >
        <h2 id="edit-disparo-title">Editar janela e cadência</h2>
        <p className="edit-disparo-modal-hint">
          Horários usam o fuso da organização. O <strong>intervalo entre envios</strong> é um atraso aleatório
          entre mensagens (1 a N minutos; com <strong>1</strong> min, ~20–60 s). Se estiver em{' '}
          <strong>Máx. atingido</strong>, aumente o limite por rodada ou ative “Disparar até o fim” para
          retomar.
        </p>
        {error && <div className="edit-disparo-modal-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="edit-disparo-modal-field">
            <label htmlFor="ed-hora-inicio">Início</label>
            <input
              id="ed-hora-inicio"
              type="time"
              value={horaInicio.length === 5 ? horaInicio : horaInicio.slice(0, 5)}
              onChange={(e) => setHoraInicio(e.target.value)}
              required
            />
          </div>
          <div className="edit-disparo-modal-field">
            <label htmlFor="ed-hora-fim">Fim</label>
            <input
              id="ed-hora-fim"
              type="time"
              value={horaFim.length === 5 ? horaFim : horaFim.slice(0, 5)}
              onChange={(e) => setHoraFim(e.target.value)}
              required
            />
          </div>
          <div className="edit-disparo-modal-field">
            <label htmlFor="ed-intervalo">Intervalo entre envios (min)</label>
            <input
              id="ed-intervalo"
              type="number"
              min={1}
              step={1}
              value={intervaloMinutos}
              onChange={(e) => setIntervaloMinutos(Number(e.target.value))}
              required
            />
          </div>
          <div className="edit-disparo-modal-field">
            <label htmlFor="ed-max">Pausar após N envios (por rodada)</label>
            <input
              id="ed-max"
              type="number"
              min={1}
              step={1}
              value={maxDisparosPorNumero}
              onChange={(e) => setMaxDisparosPorNumero(Number(e.target.value))}
              required
            />
          </div>
          <div className="edit-disparo-modal-field edit-disparo-modal-field--checkbox">
            <label htmlFor="ed-auto-resume" className="edit-disparo-modal-checkbox-label">
              <input
                id="ed-auto-resume"
                type="checkbox"
                checked={autoResumeOnMax}
                onChange={(e) => setAutoResumeOnMax(e.target.checked)}
              />
              Disparar até o fim — ao atingir o limite, reinicia contagem e continua automaticamente
            </label>
          </div>
          <div className="edit-disparo-modal-actions">
            <button type="button" className="edit-disparo-modal-cancel" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="edit-disparo-modal-save" disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
